import {Injectable} from '@angular/core'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

/** A single thing the user can do about the toast — see `show()` on why it changes the timing. */
export interface ToastAction {
  label: string
  run: () => void
}

export interface Toast {
  id: number
  type: ToastType
  message: string
  title?: string
  action?: ToastAction
  /** Set for the length of the exit animation, then the toast is removed. */
  leaving?: boolean
}

/**
 * How long each tone stays up. An error is the one worth reading twice — and often the one
 * carrying a message from the server the user may want to quote back to us — so it lingers.
 */
const DURATION: Record<ToastType, number> = {success: 4000, info: 5000, warning: 6000, error: 8000}

/** Beyond this the stack becomes a wall (mass replay fires one per run). Oldest goes first. */
const MAX_VISIBLE = 4

/** Must match the exit animation in toasts.component.ts. */
const LEAVE_MS = 200

/**
 * The app's toasts. Replaced ngx-toastr, which was a dependency for four one-line methods and
 * shipped a stylesheet that ignored the design system.
 *
 * The API is deliberately the same four methods with the same `(message, title?)` shape, so a
 * call site reads identically either way.
 */
@Injectable({providedIn: 'root'})
export class ToastService {
  /** Read by ToastsComponent, which is mounted once at the app root. */
  toasts: Toast[] = []

  private seq = 0
  private timers = new Map<number, ReturnType<typeof setTimeout>>()

  success(message: string, title?: string) { this.show('success', message, {title}) }
  error(message: string, title?: string) { this.show('error', message, {title}) }
  warning(message: string, title?: string) { this.show('warning', message, {title}) }
  info(message: string, title?: string) { this.show('info', message, {title}) }

  /**
   * The long form, for the one case the four above don't cover: a toast carrying an action.
   * That one doesn't auto-dismiss — a toast asking the user to decide something can't take the
   * decision away four seconds later. It goes when they act on it, or when they dismiss it.
   */
  show(type: ToastType, message: string, {title, action}: {title?: string, action?: ToastAction} = {}) {
    // An identical toast fired while the first is still up (a retried save, several runs
    // replayed in a row) restarts that one instead of stacking a copy — what ngx-toastr's
    // `preventDuplicates` did for us.
    const duplicate = this.toasts.find(t =>
      !t.leaving && t.type === type && t.message === message && t.title === title)
    if (duplicate) return this.arm(duplicate)

    const live = this.toasts.filter(t => !t.leaving)
    const excess = live.length - MAX_VISIBLE + 1
    if (excess > 0) live.slice(0, excess).forEach(t => this.dismiss(t.id))

    const toast: Toast = {id: ++this.seq, type, message, title, action}
    this.toasts.push(toast)
    this.arm(toast)
  }

  dismiss(id: number) {
    const toast = this.toasts.find(t => t.id === id)
    if (!toast || toast.leaving) return
    toast.leaving = true
    this.clear(id)
    setTimeout(() => this.toasts = this.toasts.filter(t => t.id !== id), LEAVE_MS)
  }

  /** Pointer is on the toast — a long error shouldn't vanish mid-read. */
  pause(id: number) { this.clear(id) }

  resume(id: number) {
    const toast = this.toasts.find(t => t.id === id)
    if (toast && !toast.leaving) this.arm(toast)
  }

  private arm(toast: Toast) {
    this.clear(toast.id)
    if (toast.action) return
    this.timers.set(toast.id, setTimeout(() => this.dismiss(toast.id), DURATION[toast.type]))
  }

  private clear(id: number) {
    clearTimeout(this.timers.get(id))
    this.timers.delete(id)
  }
}
